import { getIconProps, type IconProps } from '#types'

/**
 * Solar / lunar icons — sun, moon, sunrise, and combined sun-moon glyphs.
 *
 * Used for time-of-day indicators (morning / afternoon / night) on
 * scheduling and rate-period UI.
 */

/**
 * Sun icon — circle with 8 rays.
 */
export function SunIcon({ strokeWidth = 2, ...props }: IconProps) {
  return (
    <svg {...getIconProps(props)} viewBox="0 0 24 24">
      <circle cx="11.9983" cy="12" r="4.14286" stroke="currentColor" strokeWidth={strokeWidth} />
      <path d="M12 3.64282V4.92854" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M12 19.0714V20.3571" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M20.3555 12L19.0698 12" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M4.92969 12L3.64397 12" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5.57031 5.57153L6.47945 6.48067" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5.57031 18.6948L6.47945 17.7857" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M18.6953 5.57153L17.7862 6.48067" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M18.6953 18.6948L17.7862 17.7857" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  )
}

/**
 * Moon icon — crescent moon outline.
 */
export function MoonIcon({ strokeWidth = 2, ...props }: IconProps) {
  return (
    <svg {...getIconProps(props)} viewBox="0 0 24 24">
      <path
        d="M17.9453 14.8852C18.1076 14.6392 17.886 14.339 17.5781 14.3447C17.5313 14.3456 17.4844 14.346 17.4374 14.346C13.5736 14.346 10.4413 11.4013 10.4413 7.76878C10.4413 6.97577 10.5906 6.21554 10.8642 5.51158C10.9699 5.23964 10.7401 4.94475 10.4398 5.00889C7.32503 5.67406 5 8.29316 5 11.4228C5 15.0553 8.13222 18 11.996 18C14.5085 18 16.7116 16.7549 17.9453 14.8852Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
      />
    </svg>
  )
}

/**
 * Sunrise icon — half-sun above two horizon lines.
 */
export function SunriseIcon({ strokeWidth = 2, ...props }: IconProps) {
  return (
    <svg {...getIconProps(props)} viewBox="0 0 24 24">
      <path
        d="M8 12C8 10.9391 8.42143 9.92172 9.17157 9.17157C9.92172 8.42143 10.9391 8 12 8C13.0609 8 14.0783 8.42143 14.8284 9.17157C15.5786 9.92172 16 10.9391 16 12"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 3.64282V4.92854" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M20.3555 12L19.0698 12" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M4.92969 12L3.64397 12" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5.57031 5.57153L6.47945 6.48067" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M18.6953 5.57153L17.7862 6.48067" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M4 16L20 16" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5 19L19 19" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  )
}

/**
 * Sun + moon icon — partial sun rays with a crescent moon below.
 */
export function SunMoonIcon({ strokeWidth = 2, ...props }: IconProps) {
  return (
    <svg {...getIconProps(props)} viewBox="0 0 24 24">
      <path
        d="M12 3V4.80014"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.6003 12.0008C15.6003 11.0459 15.221 10.1302 14.5458 9.45501C13.8706 8.77983 12.9549 8.40051 12 8.40051"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.3004 5.7002L17.1699 6.83068"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.1992 12.0007H20.9994"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.9504 17.1261C15.1003 16.899 14.8957 16.6219 14.6114 16.6271C14.5682 16.6279 14.5249 16.6283 14.4815 16.6283C10.9147 16.6283 8.02316 13.9099 8.02316 10.5566C8.02316 9.82454 8.16097 9.12274 8.41352 8.47287C8.51108 8.22183 8.29895 7.94961 8.02173 8.00881C5.14634 8.62287 3 11.0407 3 13.9298C3 17.2831 5.8915 20.0015 9.45835 20.0015C11.7777 20.0015 13.8115 18.8521 14.9504 17.1261Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
      />
    </svg>
  )
}
